import App, { routes } from '@/App.tsx'
import { flattenRoutes } from '@/lib/router.ts'
import { createHead, UnheadProvider } from '@unhead/react/server'
import { renderToString } from 'react-dom/server'
import { StaticRouter } from 'react-router-dom/server'

interface PrerenderData {
  url: string
}

type PrerenderResult = string | {
  html?: string
  head?: {
    lang?: string
    title?: string
    elements?: Set<{
      type: string
      props: Record<string, string>
      children?: string
    }>
  }
  links?: Set<string> | string[]
} | null | undefined

// IMPORTANT: Prerender ALL pages to ensure Google can crawl and index them.
// 
// Google discovers pages from sitemap.xml but needs prerendered HTML to actually
// crawl and index them. Without prerendered pages, discovered URLs remain 
// unindexed (as shown in GSC with "discovered, not crawled").
//
// To prevent the original O(n^2) nav rendering issue, we now:
// 1. Limit the returned links to only core hub pages (not the full nav tree)
// 2. But still prerender every route to static HTML for SEO
//
// This allows the build to stay fast while ensuring all pages are crawlable.
const paths = flattenRoutes(routes)
  .map(route => route.path)
  .filter(path => !!path)

export async function prerender(data: PrerenderData): Promise<PrerenderResult> {
  try {
    const head = createHead({
      disableDefaults: true,
    })

    const html = renderToString(
      <UnheadProvider value={head}>
        <StaticRouter location={data.url}>
          <App />
        </StaticRouter>
      </UnheadProvider>,
    )

    // Return empty links array - the prerender plugin should use the explicit
    // routes list from vite.config.ts instead of crawling from page links.
    // This prevents the O(n^2) navigation rendering issue.
    const links: string[] = []

    const headTags = await head.resolveTags()
    const lang = headTags.find(tag => tag.tag === 'htmlAttrs')?.props.lang
    const title = headTags.find(tag => tag.tag === 'title')?.textContent
    const elements = headTags
      .filter(tag => tag.tag !== 'htmlAttrs' && tag.tag !== 'title')
      .map(tag => ({
        type: tag.tag,
        props: tag.props,
        children: tag.textContent,
      }))

    return {
      html,
      links,
      head: {
        lang,
        title,
        elements: new Set(elements),
      },
    }
  } catch (e: any) {
    console.warn(`Failed to prerender "${data.url}":`, e.message)
    return null
  }
}
